import React, { useState } from "react";

import { css } from "@emotion/css";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { useTranslation } from "react-i18next";

import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { downloadFileFromBlob } from "@/utils/fileUtils.ts";

export const GlycemiaDataManagement: React.FC = () => {
  const patientId = usePatientIdFromURL();
  const downloadCSV = Queries.qa.useExportGlycemiaCSV();
  const generateGlycemiaData = Queries.qa.useGenerateGlycemiaData();
  const deleteGlycemiaData = Queries.qa.useDeleteGlycemiaData();
  const uploadGlycemiaCSV = Queries.qa.useUploadGlycemiaCSV();

  const [selectedDiabetesType, setSelectedDiabetesType] = useState("CGM");
  const [csvToUpload, setCsvToUpload] = useState<File>();

  const styles = useStyles(makeStyles);

  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader title={t("QaTools.glycemiaDataManagement")} />
      <CardContent className={styles.space}>
        <div className={styles.menuItem}>
          <Typography variant="subtitle">{t("QaTools.downloadCSVTitle")}</Typography>
          <Button
            data-action="qa.glycemia-data.download"
            variant="contained"
            onClick={() =>
              downloadCSV.mutate(
                {
                  patientId,
                },
                {
                  onSuccess: blob => downloadFileFromBlob(blob, `data.csv`, "text/csv"),
                },
              )
            }
          >
            {t("QaTools.downloadCSV")}
          </Button>
        </div>
        <div className={styles.menuItem}>
          <Typography variant="subtitle">{t("QaTools.uploadCSVTitle")}</Typography>
          <input
            type="file"
            hidden
            onChange={event => {
              const file = event.target.files?.[0];
              if (file) {
                setCsvToUpload(file);
              }
            }}
          />
          <Button
            data-action="qa.glycemia-data.upload"
            onClick={() => csvToUpload && uploadGlycemiaCSV.mutate({ patientId, file: csvToUpload })}
            variant="contained"
            disabled={!csvToUpload}
          >
            {t("QaTools.uploadCSV")}
          </Button>
        </div>
        <div className={styles.menuItem}>
          <Typography variant="subtitle">{t("QaTools.generateDataTitle")}</Typography>
          <FormControlLabel
            control={
              <Radio
                checked={selectedDiabetesType === "BGM"}
                onChange={() => setSelectedDiabetesType("BGM")}
                value="BGM"
              />
            }
            label="BGM"
          />
          <FormControlLabel
            control={
              <Radio
                checked={selectedDiabetesType === "CGM"}
                onChange={() => setSelectedDiabetesType("CGM")}
                value="CGM"
              />
            }
            label="CGM"
          />
          <Button
            data-action="qa.glycemia-data.generate"
            variant="contained"
            onClick={() => {
              generateGlycemiaData.mutate({
                patientId,
                glycemiaType: selectedDiabetesType,
              });
            }}
          >
            {t("QaTools.generateData")}
          </Button>
        </div>
        <div className={styles.menuItem}>
          <Typography variant="subtitle">{t("QaTools.deleteDataTitle")}</Typography>
          <Button
            data-action="qa.glycemia-data.delete"
            onClick={() => deleteGlycemiaData.mutate({ patientId })}
            variant="contained"
          >
            {t("QaTools.deleteData")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  menuItem: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  space: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(12)};
  `,
});
